/**
 * Administração da revenda (task H13, épico E13).
 *
 * ── Submenu, e não quatro itens no menu lateral ───────────────────────────
 *
 * Configuração, conexões, funcionalidades e fila de descarte são coisas que a
 * mesma pessoa faz na mesma sessão, algumas vezes por mês. Espalhá-las no menu
 * principal daria a cada uma o mesmo peso do Atendimento, que é onde a revenda
 * passa o dia — e o menu de um produto ensina o que importa pelo tamanho.
 *
 * ── O segredo nunca é exibido, nem mascarado ──────────────────────────────
 *
 * A Armadilha da task: *"exibir o segredo mascarado com o valor real no HTML.
 * Basta inspecionar o elemento."*
 *
 * Aqui não há como cometê-la, e não por disciplina: o servidor **não devolve**
 * a credencial nem a referência dela (D11). O que chega é um booleano. O campo
 * mostra "configurada" e oferece substituir — nunca revelar.
 *
 * ── O motor é só leitura, e isso tem valor ────────────────────────────────
 *
 * Chatwoot e Twenty são nossos, e a credencial deles é gerada no
 * provisionamento — o admin da revenda nunca a teve e não precisa tê-la.
 * Editá-la daqui só serviria para ele apontar o próprio tenant para outro
 * lugar, ou quebrar a operação com um erro de digitação.
 *
 * Mas VER a saúde é o que separa "o sistema está lento" de "a conexão com o
 * Chatwoot está degradada desde as 14h" — e essa diferença é quem abre chamado
 * contra quem.
 */

import { useState, type ReactNode } from 'react';
import { AlvoDasAcoes } from './acao-da-secao';
import { usePapel } from '@/shared/sessao/contexto';
import type { TipoDeCanal } from '@cliqcrm/contracts';
import { DialogoDeStepUp, useStepUp } from '@/features/acesso/confirmar-identidade';
import { SecaoEtiquetas } from './etiquetas';
import { SecaoMensagensProntas } from './mensagens-prontas';
import { SecaoPessoas } from './pessoas';
import { SecaoEtapasDoFunil } from './etapas-do-funil';
import { SecaoFuncionalidades } from './loja';
import { SecaoOperadoras } from './operadoras';
import { SecaoEtapasDeExame } from './etapas-de-exame';
import { SecaoCamposPersonalizados } from './campos-personalizados';
import './administracao.css';
import { CabecalhoDeTela } from '@/shared/ui';
import { SECOES_DA_ADMINISTRACAO, type SecaoDaAdministracao } from '@/widgets/menu-lateral';
import { CONECTA_CANAIS, EDITA_FUNCIONALIDADES, VE_INTEGRACAO } from '@/entities/acesso/papel';
import { SecaoConexoes } from './secao-conexoes';
import { SecaoConfiguracao } from './secao-configuracao';
import { SecaoDescarte } from './secao-descarte';
import { SecaoSeguranca } from './secao-seguranca';

/**
 * A administração, sem menu próprio.
 *
 * ── Por que a seção vem de fora ───────────────────────────────────────────
 *
 * Ela era escolhida por um menu DENTRO da tela, ao lado do menu do produto —
 * duas colunas de navegação lado a lado, e a pessoa precisava descobrir que a
 * segunda existia. Pior: o item "Configurações" do trilho ficava aceso sem
 * dizer em qual das quatro seções ela estava.
 *
 * Agora as quatro são subitens de Configurações no trilho, e o estado sobe
 * junto: quem está aceso no menu é a seção, não a área. A tela ficou só com o
 * conteúdo, que é o que ela tem para dar.
 */
export function Administracao({
  secao,
  aoTrocarDeSecao,
}: {
  secao: SecaoDaAdministracao;
  /**
   * Troca a seção acesa no menu. Quem desenha o menu é a casca; a loja de
   * funcionalidades usa isto para levar a Conexões quem escolheu um canal.
   */
  aoTrocarDeSecao?: (secao: SecaoDaAdministracao) => void;
}): ReactNode {
  // Um só para a tela inteira: duas seções pedindo step-up ao mesmo tempo não
  // acontece — a pessoa está em uma delas —, e um por seção faria dois
  // diálogos poderem abrir juntos.
  const stepUp = useStepUp();
  const papel = usePapel();

  /*
   * O canal que a loja pediu para adicionar (#560).
   *
   * Mora aqui, e não em Conexões, porque é a ponte entre as duas seções: a loja
   * escreve, a casca troca a seção, e o assistente de Conexões abre já no
   * passo de configurar este canal — e devolve `null` ao atender.
   */
  const [canalPedido, definirCanalPedido] = useState<TipoDeCanal | null>(null);

  /*
   * O cabeçalho é montado UMA vez, aqui, e não uma por seção.
   *
   * As oito abriam com `<h2>` ou direto no conteúdo, e a barra do celular dizia
   * "Configurações" nas oito — quem chegava pelo submenu não tinha confirmação
   * de onde caiu. O `apoio` de cada seção já estava escrito em
   * `SECOES_DA_ADMINISTRACAO` e não aparecia em tela nenhuma.
   */
  const atual = SECOES_DA_ADMINISTRACAO.find((s) => s.chave === secao);
  const [alvoDasAcoes, definirAlvoDasAcoes] = useState<HTMLElement | null>(null);

  return (
    <div className="admin">
      <DialogoDeStepUp uso={stepUp} />

      <CabecalhoDeTela
        titulo={atual?.nome ?? 'Configurações'}
        apoio={atual?.apoio}
        acoes={
          /*
            O ALVO do portal da seção — ver `acao-da-secao.tsx`.
            
            Ele fica dentro do slot de ações, e não é o slot: o componente é
            quem desenha a fileira, e cada seção pendura aqui a ação dela
            ("Convidar pessoa", "Nova etapa") sem saber que existe um
            cabeçalho.
          */
          <div className="admin__alvo-das-acoes" ref={definirAlvoDasAcoes} />
        }
      />

      <AlvoDasAcoes.Provider value={alvoDasAcoes}>
        <div className="admin__conteudo">
          {secao === 'configuracao' && <SecaoConfiguracao stepUp={stepUp} />}
          {secao === 'pessoas' && <SecaoPessoas />}
          {secao === 'etiquetas' && <SecaoEtiquetas />}
          {secao === 'mensagens-prontas' && <SecaoMensagensProntas />}
          {secao === 'etapas' && <SecaoEtapasDoFunil />}
          {secao === 'campos-personalizados' && <SecaoCamposPersonalizados />}
          {secao === 'seguranca' && <SecaoSeguranca />}
          {secao === 'conexoes' && (
            <SecaoConexoes
              stepUp={stepUp}
              canalPedido={canalPedido}
              aoAtenderPedido={() => definirCanalPedido(null)}
            />
          )}
          {secao === 'funcionalidades' && (
            <SecaoFuncionalidades
              stepUp={stepUp}
              veIntegracao={papel != null && VE_INTEGRACAO.has(papel)}
              editaFuncionalidades={papel != null && EDITA_FUNCIONALIDADES.has(papel)}
              conectaCanais={papel != null && CONECTA_CANAIS.has(papel)}
              {...(aoTrocarDeSecao
                ? {
                    aoAdicionarCanal: (canal: TipoDeCanal) => {
                      definirCanalPedido(canal);
                      aoTrocarDeSecao('conexoes');
                    },
                    aoAbrirConexoes: () => aoTrocarDeSecao('conexoes'),
                  }
                : {})}
            />
          )}
          {secao === 'operadoras' && <SecaoOperadoras />}
          {secao === 'etapas-de-exame' && <SecaoEtapasDeExame />}
          {secao === 'descarte' && <SecaoDescarte />}
        </div>
      </AlvoDasAcoes.Provider>
    </div>
  );
}

// ── Configuração (D13) ─────────────────────────────────────────────────────
